import React, { useCallback, useMemo, useState } from 'react';

import { useAsync } from 'react-use';
import { SuggestionStatus } from 'src/components/ai/chat/chat-types';
import { useUpdateSuggestionStatusInChat } from 'src/components/ai/chat/ChatPanelMarkdown/elements/CodeElement/CodeSuggestionListElement/handle-suggestions-hooks';
import {
  DocumentTaskReviewDock,
  useReviewTaskIdParam,
} from 'src/components/workflow/workflow-detail/side-panel/task-review/DocumentTaskReviewDock';
import {
  EditorSuggestionStateProvider,
  getIsSuggestionsAlertVisible,
} from 'src/editor/editor-suggestion-state-registry';
import type { EditorSuggestionState } from 'src/editor/editor-suggestion-state-registry';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';

import { ContractAlert } from './ContractAlert';
import type { SuggestionUnit } from './suggestion-units';
import { useComputeSuggestionUnits } from './suggestion-units';
import { SuggestionsAlert } from './SuggestionsAlert';
import type { EditorAlertProps } from './types';
import { useComputeSuggestionMarkers } from './useComputeSuggestionMarkers';
import { useClearSelectedSuggestionMarkerOnSelectionChange } from './useUpdateSelectedSuggestionMarkerIndex';

export function indexUnitGroupIdsByMessageId(units: SuggestionUnit[]): Record<string, string[]> {
  return units.reduce<Record<string, string[]>>((acc, unit) => {
    if (!unit.messageId) return acc;
    if (!acc[unit.messageId]) acc[unit.messageId] = [];
    acc[unit.messageId].push(unit.groupId);

    return acc;
  }, {});
}

export function EditorAlert({ document }: EditorAlertProps): JSX.Element | null {
  const tabId = useCurrentTabIdOrThrow();
  const { updateSuggestionStatusInChat: updateAISuggestions } = useUpdateSuggestionStatusInChat();

  const [handleAllAiEdits, setHandleAllAiEdits] = useState(false);
  const { recomputeSuggestions, suggestionMarkers } = useComputeSuggestionMarkers();
  const suggestionUnits = useComputeSuggestionUnits(suggestionMarkers);
  const [selectedSuggestionGroupId, setSelectedSuggestionGroupId] = useState<string | null>(null);

  const setHandleAllAiEditsCallback = useCallback(
    (value: boolean) => setHandleAllAiEdits(value),
    []
  );
  const setSelectedSuggestionGroupIdCallback = useCallback(
    (suggestionId: string | null) => setSelectedSuggestionGroupId(suggestionId),
    []
  );

  useClearSelectedSuggestionMarkerOnSelectionChange({
    selectedSuggestionGroupId,
    setSelectedSuggestionGroupId: setSelectedSuggestionGroupIdCallback,
    suggestionUnits,
  });

  const suggestionState = useMemo<EditorSuggestionState>(
    () => ({
      handleAllAiEdits,
      recomputeSuggestions,
      selectedSuggestionGroupId,
      setHandleAllAiEdits: setHandleAllAiEditsCallback,
      setSelectedSuggestionGroupId: setSelectedSuggestionGroupIdCallback,
      suggestionUnits,
    }),
    [
      handleAllAiEdits,
      recomputeSuggestions,
      selectedSuggestionGroupId,
      setHandleAllAiEditsCallback,
      setSelectedSuggestionGroupIdCallback,
      suggestionUnits,
    ]
  );

  useAsync(async () => {
    if (!suggestionUnits.length) return;

    const groupIdsByMessageId = indexUnitGroupIdsByMessageId(suggestionUnits);

    await Promise.all(
      Object.entries(groupIdsByMessageId).map(async ([messageId, groupIds]) => {
        await updateAISuggestions({
          groupIds,
          messageId,
          status: SuggestionStatus.Pending,
        });
      })
    );
  }, [suggestionUnits]);

  return (
    <EditorSuggestionStateProvider state={suggestionState} tabId={tabId}>
      <ActiveEditorAlert
        document={document}
        showSuggestions={getIsSuggestionsAlertVisible({ suggestionUnits })}
      />
    </EditorSuggestionStateProvider>
  );
}

/** One floating alert at a time: pending AI suggestions, then an open review, then the contract's. */
function ActiveEditorAlert({
  document,
  showSuggestions,
}: EditorAlertProps & { showSuggestions: boolean }): JSX.Element {
  const reviewTaskId = useReviewTaskIdParam();
  if (showSuggestions) return <SuggestionsAlert />;
  if (reviewTaskId)
    return (
      <DocumentTaskReviewDock
        fallback={<ContractAlert document={document} />}
        taskId={reviewTaskId}
      />
    );

  return <ContractAlert document={document} />;
}
